import React from 'react';
import T from 'prop-types';
import cx from 'classnames';

import Tooltip from '../../../../components/Tooltip';
import Skeleton from '../../../../components/Skeleton';
import useHeatmapConfig from '../hooks/useHeatmapConfig';
import { formatValue } from '../utils/heatmap';
import { format, endOfMonth, isSame } from '../../../../utils/date';

const HeatmapTable = ({
  months,
  data,
  maxDate,
  className,
  leadHeader,
  getRowKey,
  renderLeadCell,
}) => {
  const { yearGroups, lastMonthIndex, getBackground } = useHeatmapConfig({ months, data });

  return (
    <table className={cx('dashboard-heatmap', className)}>
      <thead>
        <tr className="dashboard-heatmap__year-row">
          <th aria-label="Row label column" />
          {yearGroups.map(({ year, count }) => (
            <th key={year} colSpan={count}>
              {year}
            </th>
          ))}
        </tr>
        <tr className="dashboard-heatmap__month-row">
          <th>{leadHeader}</th>
          {months.map((date, i) => (
            <th key={date}>
              {i === lastMonthIndex && !isSame(maxDate, endOfMonth(date)) ? (
                <div className="d-flex align-items-center">
                  <span>{format(date, 'MMM')} (partial)</span>{' '}
                  <Tooltip content="Partial month" placement="top" colorVariant="light">
                    <span className="hicon-question-circle" />
                  </Tooltip>
                </div>
              ) : (
                format(date, 'MMM')
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row) => (
          <tr key={getRowKey(row)}>
            {renderLeadCell(row)}
            {row.values.map((value, i) => {
              const isPartialCell =
                i === row.values.length - 1 && !isSame(maxDate, endOfMonth(months[i]));
              return (
                <td
                  key={months[i]}
                  style={{ backgroundColor: getBackground(value) }}
                  className={cx({ 'is-partial': isPartialCell })}
                >
                  {formatValue(value)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

HeatmapTable.propTypes = {
  months: T.arrayOf(T.string).isRequired,
  data: T.arrayOf(
    T.shape({
      values: T.arrayOf(T.number),
    })
  ).isRequired,
  maxDate: T.string.isRequired,
  className: T.string,
  leadHeader: T.node,
  getRowKey: T.func.isRequired,
  renderLeadCell: T.func.isRequired,
};

export default HeatmapTable;

export const HeatmapTableSkeleton = ({ height, className }) => (
  <div className={cx('d-flex p-mdl', className)}>
    <Skeleton height={height} />
  </div>
);

export const HeatmapEmptyState = ({ className, children = 'No data available' }) => (
  <div className={cx('d-flex p-mdl', className)}>
    <div className="dashboard-heatmap__empty">{children}</div>
  </div>
);

HeatmapTableSkeleton.propTypes = {
  height: T.number,
  className: T.string,
};

HeatmapEmptyState.propTypes = {
  className: T.string,
  children: T.node,
};
